import React, { Suspense, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import "./NeuronImpactMap.css";

const REGION_CONFIG = [
  {
    key: "frontal",
    label: "Frontal Lobe",
    short: "Frontal",
    type: "Surface region",
    color: "#ff4fa3",
    palette: ["#ff4fa3", "#ff2f92", "#ff76b8", "#ff9ed0"],
    count: 260,
    description: "Planning, reasoning, judgment, attention, and behavior.",
    anchors: [
      { center: [-1.08, 0.18, 0.32], spread: [0.7, 0.72, 0.52], weight: 1.1 },
      { center: [-0.85, 0.58, 0.26], spread: [0.62, 0.46, 0.5], weight: 0.85 },
      { center: [-0.78, -0.18, 0.28], spread: [0.5, 0.38, 0.46], weight: 0.55 },
    ],
  },
  {
    key: "temporal",
    label: "Temporal Lobe",
    short: "Temporal",
    type: "Surface region",
    color: "#ff7a1a",
    palette: ["#ff7a1a", "#ff9b38", "#ffb84c", "#ff5e00"],
    count: 230,
    description: "Hearing, memory, and language processing.",
    anchors: [
      { center: [-0.96, -0.48, 0.24], spread: [0.76, 0.45, 0.42], weight: 1.1 },
      { center: [-0.58, -0.62, 0.18], spread: [0.54, 0.34, 0.38], weight: 0.75 },
      { center: [-1.2, -0.22, 0.22], spread: [0.36, 0.3, 0.34], weight: 0.45 },
    ],
  },
  {
    key: "parietal",
    label: "Parietal Lobe",
    short: "Parietal",
    type: "Surface region",
    color: "#d7ff55",
    palette: ["#d7ff55", "#eaff7a", "#fff28a", "#b8ff4d"],
    count: 285,
    description: "Sensory integration, body awareness, spatial processing.",
    anchors: [
      { center: [-0.18, 0.74, 0.32], spread: [0.78, 0.43, 0.52], weight: 1 },
      { center: [0.34, 0.66, 0.28], spread: [0.7, 0.4, 0.48], weight: 0.95 },
      { center: [-0.58, 0.48, 0.3], spread: [0.5, 0.34, 0.42], weight: 0.65 },
    ],
  },
  {
    key: "occipital",
    label: "Occipital Lobe",
    short: "Occipital",
    type: "Surface region",
    color: "#2f80ff",
    palette: ["#2f80ff", "#43a4ff", "#58d5ff", "#6ea8ff"],
    count: 265,
    description: "Visual processing center.",
    anchors: [
      { center: [0.9, 0.25, 0.24], spread: [0.6, 0.7, 0.48], weight: 1.05 },
      { center: [0.82, 0.62, 0.2], spread: [0.42, 0.42, 0.42], weight: 0.6 },
      { center: [1.14, -0.08, 0.2], spread: [0.46, 0.42, 0.4], weight: 0.55 },
    ],
  },
  {
    key: "hippocampus",
    label: "Hippocampus",
    short: "Hippocampus",
    type: "Deep structure",
    color: "#2dd4bf",
    palette: ["#2dd4bf", "#20f7dc", "#67e8f9", "#14b8a6"],
    count: 130,
    description: "Memory formation and recall.",
    anchors: [
      { center: [-0.18, 0.02, 0.58], spread: [0.46, 0.3, 0.36], weight: 1 },
      { center: [0.1, -0.02, 0.52], spread: [0.35, 0.26, 0.32], weight: 0.75 },
    ],
  },
  {
    key: "amygdala",
    label: "Amygdala",
    short: "Amygdala",
    type: "Deep structure",
    color: "#ff4040",
    palette: ["#ff4040", "#ff5d5d", "#ff2d55", "#ff7070"],
    count: 105,
    description: "Emotion, fear, and stress response.",
    anchors: [
      { center: [0.28, -0.06, 0.58], spread: [0.35, 0.26, 0.34], weight: 1 },
      { center: [0.06, -0.16, 0.48], spread: [0.26, 0.22, 0.28], weight: 0.65 },
    ],
  },
  {
    key: "basal_ganglia",
    label: "Basal Ganglia",
    short: "Basal Ganglia",
    type: "Deep structure",
    color: "#6d5dfc",
    palette: ["#6d5dfc", "#8b5cf6", "#a78bfa", "#4f46e5"],
    count: 135,
    description: "Movement regulation and motor coordination loops.",
    anchors: [
      { center: [0.0, -0.2, 0.38], spread: [0.46, 0.32, 0.36], weight: 1 },
      { center: [0.28, -0.24, 0.32], spread: [0.34, 0.26, 0.3], weight: 0.65 },
    ],
  },
  {
    key: "brainstem",
    label: "Brainstem",
    short: "Brainstem",
    type: "Deep structure",
    color: "#34d399",
    palette: ["#34d399", "#22c55e", "#86efac", "#10b981"],
    count: 120,
    description: "Autonomic control, relay center, vital pathways.",
    anchors: [
      { center: [0.06, -0.78, 0.08], spread: [0.2, 0.52, 0.22], weight: 1.1 },
      { center: [0.1, -1.08, 0.0], spread: [0.16, 0.44, 0.16], weight: 0.8 },
    ],
  },
  {
    key: "cerebellum",
    label: "Cerebellum",
    short: "Cerebellum",
    type: "Surface region",
    color: "#8b5cf6",
    palette: ["#8b5cf6", "#7c3aed", "#a855f7", "#c084fc"],
    count: 185,
    description: "Balance, coordination, and posture.",
    anchors: [
      { center: [0.48, -0.56, -0.2], spread: [0.62, 0.4, 0.42], weight: 1.1 },
      { center: [0.82, -0.38, -0.14], spread: [0.38, 0.32, 0.32], weight: 0.65 },
    ],
  },
  {
    key: "spinal_cord",
    label: "Spinal Cord",
    short: "Spinal Cord",
    type: "Deep structure",
    color: "#7dd3fc",
    palette: ["#7dd3fc", "#38bdf8", "#bae6fd", "#0ea5e9"],
    count: 75,
    description: "Signal pathway from brain to body.",
    anchors: [{ center: [0.08, -1.28, 0.0], spread: [0.13, 0.56, 0.13], weight: 1 }],
  },
];

const REGION_ALIASES = {
  frontal: "frontal",
  "frontal lobe": "frontal",
  "frontal cortex": "frontal",
  "prefrontal cortex": "frontal",
  temporal: "temporal",
  "temporal lobe": "temporal",
  parietal: "parietal",
  "parietal lobe": "parietal",
  occipital: "occipital",
  "occipital lobe": "occipital",
  "occipital cortex": "occipital",
  hippocampus: "hippocampus",
  amygdala: "amygdala",
  "basal ganglia": "basal_ganglia",
  basal_ganglia: "basal_ganglia",
  brainstem: "brainstem",
  "brain stem": "brainstem",
  cerebellum: "cerebellum",
  "spinal cord": "spinal_cord",
  spinal_cord: "spinal_cord",
};

const REGION_MAP = new Map(REGION_CONFIG.map((region) => [region.key, region]));

function seededRandom(seed) {
  const x = Math.sin(seed * 9999.719) * 10000;
  return x - Math.floor(x);
}

function weightedAnchor(region, seed) {
  const anchors = region.anchors || [];
  const total = anchors.reduce((sum, item) => sum + (item.weight || 1), 0);
  let roll = seededRandom(seed) * total;

  for (const anchor of anchors) {
    roll -= anchor.weight || 1;
    if (roll <= 0) return anchor;
  }

  return anchors[0];
}

function ellipsoidPoint(anchor, seed, regionType) {
  const u = seededRandom(seed + 1);
  const v = seededRandom(seed + 2);
  const w = seededRandom(seed + 3);

  const theta = 2 * Math.PI * u;
  const phi = Math.acos(2 * v - 1);
  const radius = Math.cbrt(w);

  let x = Math.sin(phi) * Math.cos(theta);
  let y = Math.cos(phi);
  let z = Math.sin(phi) * Math.sin(theta);

  if (regionType === "Surface region") {
    const shell = 0.62 + seededRandom(seed + 4) * 0.38;
    x *= shell;
    y *= shell;
    z *= shell;
  }

  return [
    anchor.center[0] + x * radius * anchor.spread[0],
    anchor.center[1] + y * radius * anchor.spread[1],
    anchor.center[2] + z * radius * anchor.spread[2],
  ];
}

function colorToArray(hex, brighten = 0) {
  const color = new THREE.Color(hex);
  if (brighten > 0) color.lerp(new THREE.Color("#ffffff"), brighten);
  return [color.r, color.g, color.b];
}

function mapAnalysisRegions(analysis) {
  const raw = [
    ...(analysis?.brain_regions || []),
    ...(analysis?.structured?.impacted_regions || []),
  ];

  const found = new Map();

  raw.forEach((item) => {
    const text = typeof item === "string" ? item : item?.label || item?.region || item?.id || "";
    const normalized = String(text).toLowerCase();

    Object.entries(REGION_ALIASES).forEach(([alias, key]) => {
      if (normalized.includes(alias.replace("_", " "))) {
        const region = REGION_MAP.get(key);
        if (!region || found.has(key)) return;

        found.set(key, {
          ...region,
          why: typeof item === "string" ? item : item?.why || region.description,
          intensity: item?.intensity || "moderate",
        });
      }
    });
  });

  if (found.size === 0) {
    REGION_CONFIG.forEach((region) => {
      found.set(region.key, {
        ...region,
        why: region.description,
        intensity: "moderate",
      });
    });
  }

  return Array.from(found.values());
}

function createNeuronNetwork(activeRegions) {
  const nodes = [];
  const links = [];
  const regionBuckets = new Map();

  activeRegions.forEach((region, regionIndex) => {
    const bucket = [];

    for (let i = 0; i < region.count; i += 1) {
      const seed = regionIndex * 100000 + i * 53;
      const anchor = weightedAnchor(region, seed + 9);
      const palette = region.palette || [region.color];
      const color = palette[Math.floor(seededRandom(seed + 10) * palette.length) % palette.length];
      const glow = seededRandom(seed + 11) > 0.92;

      const node = {
        id: `${region.key}-${i}`,
        regionKey: region.key,
        color,
        baseColor: region.color,
        glow,
        position: ellipsoidPoint(anchor, seed, region.type),
      };

      bucket.push(nodes.length);
      nodes.push(node);
    }

    regionBuckets.set(region.key, bucket);

    for (let i = 0; i < bucket.length; i += 1) {
      const a = bucket[i];
      if (i + 1 < bucket.length && i % 3 === 0) links.push([a, bucket[i + 1], region.color]);
      if (i + 5 < bucket.length && i % 7 === 0) links.push([a, bucket[i + 5], region.color]);
      if (i + 17 < bucket.length && i % 19 === 0) links.push([a, bucket[i + 17], region.color]);
    }
  });

  for (let i = 0; i < activeRegions.length - 1; i += 1) {
    const fromBucket = regionBuckets.get(activeRegions[i].key) || [];
    const toBucket = regionBuckets.get(activeRegions[i + 1].key) || [];

    for (let j = 0; j < 8; j += 1) {
      const fromIndex = fromBucket[Math.floor(seededRandom(i * 131 + j) * fromBucket.length)];
      const toIndex = toBucket[Math.floor(seededRandom(i * 241 + j) * toBucket.length)];
      if (fromIndex !== undefined && toIndex !== undefined) {
        links.push([fromIndex, toIndex, activeRegions[i].color]);
      }
    }
  }

  return { nodes, links };
}

function BrainModel() {
  const { scene } = useGLTF("/brain.glb");

  const model = useMemo(() => {
    const clone = scene.clone(true);

    clone.traverse((child) => {
      if (child.isMesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: "#ff9a9a",
          transparent: true,
          opacity: 0.15,
          roughness: 0.95,
          metalness: 0.02,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
      }
    });

    return clone;
  }, [scene]);

  return (
    <primitive
      object={model}
      scale={1.96}
      position={[0, -0.24, 0]}
      rotation={[0.04, -0.3, 0]}
    />
  );
}

function NeuronNetwork({ activeRegions, selectedKey }) {
  const { nodes, links } = useMemo(() => createNeuronNetwork(activeRegions), [activeRegions]);
  const pointsRef = useRef(null);
  const glowRef = useRef(null);
  const lineRef = useRef(null);

  const geometryData = useMemo(() => {
    const positions = [];
    const colors = [];
    const glowPositions = [];
    const glowColors = [];
    const linePositions = [];
    const lineColors = [];

    nodes.forEach((node) => {
      const selected = !selectedKey || node.regionKey === selectedKey;
      const [r, g, b] = colorToArray(node.color, selected ? 0.05 : 0.0);
      positions.push(...node.position);
      colors.push(r, g, b);

      if (node.glow || selected) {
        glowPositions.push(...node.position);
        glowColors.push(...colorToArray(node.color, 0.04));
      }
    });

    links.slice(0, selectedKey ? 750 : 1250).forEach(([fromIndex, toIndex, lineColor]) => {
      const from = nodes[fromIndex];
      const to = nodes[toIndex];
      if (!from || !to) return;

      const selected = !selectedKey || from.regionKey === selectedKey || to.regionKey === selectedKey;
      if (selectedKey && !selected) return;

      const color = colorToArray(lineColor || from.color, selected ? 0.04 : 0.0);
      linePositions.push(...from.position, ...to.position);
      lineColors.push(...color, ...color);
    });

    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(colors),
      glowPositions: new Float32Array(glowPositions),
      glowColors: new Float32Array(glowColors),
      linePositions: new Float32Array(linePositions),
      lineColors: new Float32Array(lineColors),
    };
  }, [nodes, links, selectedKey]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (pointsRef.current) pointsRef.current.rotation.y = Math.sin(t * 0.12) * 0.01;
    if (glowRef.current) {
      glowRef.current.rotation.y = Math.sin(t * 0.13) * 0.01;
      glowRef.current.material.opacity = 0.34 + Math.sin(t * 2.1) * 0.08;
    }
    if (lineRef.current) lineRef.current.material.opacity = selectedKey ? 0.28 : 0.11 + Math.sin(t * 1.6) * 0.025;
  });

  return (
    <>
      <lineSegments ref={lineRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={geometryData.linePositions.length / 3} array={geometryData.linePositions} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={geometryData.lineColors.length / 3} array={geometryData.lineColors} itemSize={3} />
        </bufferGeometry>
        <lineBasicMaterial vertexColors transparent opacity={selectedKey ? 0.28 : 0.12} blending={THREE.AdditiveBlending} depthWrite={false} />
      </lineSegments>

      <points ref={glowRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={geometryData.glowPositions.length / 3} array={geometryData.glowPositions} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={geometryData.glowColors.length / 3} array={geometryData.glowColors} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial vertexColors size={0.027} sizeAttenuation transparent opacity={0.34} blending={THREE.AdditiveBlending} depthWrite={false} />
      </points>

      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={geometryData.positions.length / 3} array={geometryData.positions} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={geometryData.colors.length / 3} array={geometryData.colors} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial vertexColors size={0.0095} sizeAttenuation transparent opacity={selectedKey ? 0.9 : 0.82} blending={THREE.AdditiveBlending} depthWrite={false} />
      </points>
    </>
  );
}

function BrainScene({ activeRegions, selectedKey }) {
  return (
    <Canvas camera={{ position: [0, 0.02, 2.52], fov: 36 }} dpr={[1, 1.7]}>
      <color attach="background" args={["#030814"]} />
      <ambientLight intensity={1.25} />
      <directionalLight position={[2, 3, 4]} intensity={1.4} />
      <pointLight position={[0, 0.35, 1.7]} intensity={1.45} color="#8b5cf6" />

      <Suspense fallback={null}>
        <Environment preset="city" />
        <BrainModel />
        <NeuronNetwork activeRegions={activeRegions} selectedKey={selectedKey} />
      </Suspense>

      <OrbitControls enableRotate enableZoom enablePan rotateSpeed={0.78} zoomSpeed={0.9} panSpeed={0.65} minDistance={1.18} maxDistance={7} autoRotate={!selectedKey} autoRotateSpeed={0.16} />
    </Canvas>
  );
}

export default function NeuronImpactMap({ analysis }) {
  const activeRegions = useMemo(() => mapAnalysisRegions(analysis), [analysis]);
  const [mode, setMode] = useState("network");
  const [selectedKey, setSelectedKey] = useState(null);

  const selectedRegion = activeRegions.find((r) => r.key === selectedKey) || activeRegions[0];
  const visibleSelectedKey = mode === "focus" ? selectedRegion?.key : null;

  return (
    <div className="neuron-map-page">
      <div className="neuron-main-panel">
        <div className="neuron-map-header">
          <div>
            <h2>Neuron Impact Map</h2>
            <p>Medgen-inferred neuron-style overlay on the brain model.</p>
          </div>

          <div className="neuron-help">
            <span>🖐 Drag to rotate</span>
            <span>🖱 Scroll to zoom</span>
            <span>↔ Right drag to pan</span>
          </div>
        </div>

        <div className="neuron-mode-toggle">
          <button className={mode === "network" ? "active" : ""} type="button" onClick={() => { setMode("network"); setSelectedKey(null); }}>
            Neuron Network View
          </button>

          <button className={mode === "focus" ? "active" : ""} type="button" onClick={() => { setMode("focus"); setSelectedKey(selectedRegion?.key || activeRegions[0]?.key); }}>
            Region Focus
          </button>
        </div>

        <div className="neuron-canvas-shell">
          <BrainScene activeRegions={activeRegions} selectedKey={visibleSelectedKey} />
        </div>

        <div className="neuron-legend">
          {activeRegions.map((region) => (
            <button key={region.key} type="button" onClick={() => { setMode("focus"); setSelectedKey(region.key); }}>
              <i style={{ background: region.color, color: region.color }} />
              {region.short}
            </button>
          ))}
        </div>
      </div>

      <aside className="neuron-side-panel">
        <section className="neuron-side-card">
          <h3>Impacted Neuron Regions</h3>
          <div className="neuron-region-grid">
            {activeRegions.map((region) => (
              <button key={region.key} type="button" className={selectedRegion?.key === region.key ? "active" : ""} onClick={() => { setMode("focus"); setSelectedKey(region.key); }}>
                <i style={{ background: region.color, color: region.color }} />
                <strong>{region.label}</strong>
                <span>{region.type} • {region.intensity || "moderate"}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="neuron-side-card selected-card">
          <h3>Selected Region</h3>
          {selectedRegion ? (
            <div className="neuron-selected-box">
              <div className="neuron-selected-head">
                <i style={{ background: selectedRegion.color, color: selectedRegion.color }} />
                <div>
                  <strong>{selectedRegion.label}</strong>
                  <p>{selectedRegion.description}</p>
                </div>
              </div>
              <div className="neuron-selected-meta">
                <div>
                  <h4>Why it was mapped</h4>
                  <p>{selectedRegion.why}</p>
                </div>
                <div>
                  <h4>Intensity</h4>
                  <span>{selectedRegion.intensity || "moderate"}</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="muted">No selected region.</p>
          )}
        </section>

        <section className="neuron-side-card raw">
          <h3>Raw Medgen region output</h3>
          {analysis?.brain_regions?.length ? (
            <ul>
              {analysis.brain_regions.map((item, index) => (
                <li key={index}>{typeof item === "string" ? item : item.label || item.region || item.id}</li>
              ))}
            </ul>
          ) : (
            <p>No region output was returned by Medgen.</p>
          )}
        </section>
      </aside>
    </div>
  );
}

useGLTF.preload("/brain.glb");
